// === Stage tracking dla diagnostyki "biały ekran na iOS" ===
// Zapisujemy etap startu do window.__bootStage – fallback w index.html odczyta to,
// gdy minie timeout, i pokaże użytkownikowi konkretny etap zamiast pustego spinnera.
type WinWithBoot = Window & {
  __bootStage?: string;
  __lovClearBoot?: () => void;
};
const setStage = (s: string) => {
  try { (window as unknown as WinWithBoot).__bootStage = s; } catch { /* noop */ }
};
setStage("module-evaluating");

import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import { AppErrorBoundary } from "./components/AppErrorBoundary";

setStage("imports-react-done");

import "./index.css";

setStage("imports-css-done");

// Hero LCP image – preload tylko na stronie głównej (jedyny widok z hero).
if (typeof document !== "undefined" && window.location.pathname === "/") {
  void import("@/assets/hero-bg-640.webp").then(({ default: heroBg640 }) =>
    Promise.all([
      import("@/assets/hero-bg-1024.webp"),
      import("@/assets/hero-bg.webp"),
    ]).then(([{ default: heroBg1024 }, { default: heroBg }]) => {
      try {
        const link = document.createElement("link");
        link.rel = "preload";
        link.as = "image";
        link.href = heroBg;
        link.setAttribute("imagesrcset", `${heroBg640} 640w, ${heroBg1024} 1024w, ${heroBg} 1536w`);
        link.setAttribute("imagesizes", "100vw");
        (link as HTMLLinkElement & { fetchPriority?: string }).fetchPriority = "high";
        document.head.appendChild(link);
      } catch { /* noop */ }
    })
  );
}

// Preload tła karty udostępniania w tle.
import shareCardBg from "@/assets/share-card-bg.png";
if (typeof window !== "undefined") {
  const preloadShare = () => {
    try { const img = new Image(); img.src = shareCardBg; } catch { /* noop */ }
  };
  // requestIdleCallback nie istnieje na iOS Safari < 17 – fallback na setTimeout.
  const ric = (window as unknown as { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback;
  if (typeof ric === "function") ric(preloadShare);
  else setTimeout(preloadShare, 1500);
}

setStage("preloads-done");

// Globalna obsługa „chunk load error" – jednorazowy hard reload.
if (typeof window !== "undefined") {
  const RELOAD_FLAG = "lov_chunk_reload_at";
  const isChunkErr = (msg: string) =>
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg);
  const tryReload = () => {
    try {
      const last = Number(sessionStorage.getItem(RELOAD_FLAG) || "0");
      const now = Date.now();
      if (now - last > 30_000) {
        sessionStorage.setItem(RELOAD_FLAG, String(now));
        window.location.reload();
      }
    } catch {
      window.location.reload();
    }
  };
  window.addEventListener("error", (e) => {
    if (isChunkErr(e.message || "")) tryReload();
  });
  window.addEventListener("unhandledrejection", (e) => {
    const msg = (e.reason && (e.reason.message || String(e.reason))) || "";
    if (isChunkErr(msg)) tryReload();
  });
}

setStage("listeners-done");

// Anuluj timer fallbacku zaraz po zamontowaniu Reacta.
try { (window as unknown as WinWithBoot).__lovClearBoot?.(); } catch { /* noop */ }

setStage("creating-root");

const rootEl = document.getElementById("root");
if (!rootEl) {
  setStage("error-no-root-el");
  throw new Error("Brak elementu #root w DOM");
}

createRoot(rootEl).render(
  <AppErrorBoundary>
    <App />
  </AppErrorBoundary>
);

setStage("render-called");

